/**
 * FilterUpkeepMonitoring Component
 * 
 * Filter component for Upkeep Budget Monitoring with year, month, estate, and division
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Text,
    Flex,
    Grid,
    useTheme,
    Select,
    HStack,
    VStack,
    FormControl,
    FormLabel,
    IconButton,
    Tooltip,
} from '@chakra-ui/react';
import { MdClear, MdRefresh } from 'react-icons/md';
import { FiSearch, FiDownload } from 'react-icons/fi';
import api from '../../../../auth/apiClient';
import { UpkeepBudgetFilters, EstateOption, DivisionOption, CompanyOption } from '../types';
import { getMonthOptions, getYearOptions } from '../utils';
import { UPKEEP_MONITORING_DEFAULTS } from '../apiConfig';

interface FilterUpkeepMonitoringProps {
    filters: UpkeepBudgetFilters;
    onFilterChange: (filters: UpkeepBudgetFilters) => void;
    onClearFilters: () => void;
    onApplyFilters: (filters: UpkeepBudgetFilters) => void;
    onExport?: () => void;
    onRefresh?: () => void;
    isExporting?: boolean;
}

const FilterUpkeepMonitoring: React.FC<FilterUpkeepMonitoringProps> = ({
    filters,
    onFilterChange,
    onClearFilters,
    onApplyFilters,
    onExport,
    onRefresh,
    isExporting = false,
}) => {
    const theme = useTheme();
    const [localFilters, setLocalFilters] = useState<UpkeepBudgetFilters>(filters);
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [estates, setEstates] = useState<EstateOption[]>([]);
    const [divisions, setDivisions] = useState<DivisionOption[]>([]);
    const [allDivisions, setAllDivisions] = useState<DivisionOption[]>([]);

    const monthOptions = getMonthOptions();
    const yearOptions = getYearOptions();

    // Style constants
    const inputStyles = {
        size: 'sm' as const,
        borderRadius: 'lg',
        borderColor: 'gray.200',
        _hover: { borderColor: 'gray.300' },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    const filteredEstates = useMemo(() => {
        if (!localFilters.company_id) return estates;
        return estates.filter((estate) => estate.company_id === localFilters.company_id);
    }, [estates, localFilters.company_id]);

    // Fetch options on component mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [optionsResponse, estatesDbResponse] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db'),
                ]);
                const optionsData = optionsResponse.data;
                const estatesDbData = estatesDbResponse.data;

                setCompanies(optionsData.companies || []);
                setAllDivisions(optionsData.divisis || []);

                // Merge estates from estate-db with odoo_id from options
                const estatesByOdooId = new Map(
                    (optionsData.estates || []).map((est: any) => [est.odoo_id, est.odoo_id])
                );

                const mergedEstates: EstateOption[] = (estatesDbData || []).map((estateDb: any) => {
                    // Find matching estate from options by name
                    const optionsEstate = (optionsData.estates || []).find(
                        (est: any) => est.name === estateDb.name
                    );

                    return {
                        id: estateDb.id,
                        name: estateDb.name,
                        company_id: estateDb.company_id,
                        odoo_id: optionsEstate?.odoo_id || estateDb.id, // Fallback to id if not found
                    };
                });

                setEstates(mergedEstates);
            } catch (error) {
                console.error('Error fetching options:', error);
            }
        };
        fetchOptions();
    }, []);

    // Filter divisions when estate changes
    useEffect(() => {
        if (!localFilters.estate_id) {
            setDivisions([]);
            return;
        }

        // Find selected estate by odoo_id to get its database id for filtering divisions
        const selectedEstate = estates.find(e => e.odoo_id === localFilters.estate_id);
        if (selectedEstate) {
            const filteredDivisions = allDivisions.filter(
                (div: DivisionOption) => div.estate_id === selectedEstate.id
            );
            setDivisions(filteredDivisions);
        }
    }, [localFilters.estate_id, estates, allDivisions]);

    // Sync local filters when filters prop changes
    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleInputChange = useCallback(
        (field: keyof UpkeepBudgetFilters, value: string | number | undefined | null) => {
            const newFilters = { ...localFilters };

            // Handle estate change - reset division when estate changes
            if (field === 'estate_id') {
                newFilters.estate_id = value ? Number(value) : undefined;
                newFilters.division_id = undefined; // Reset division when estate changes
            } else if (field === 'division_id') {
                newFilters.division_id = value ? Number(value) : undefined;
            } else if (field === 'company_id') {
                newFilters.company_id = value ? Number(value) : UPKEEP_MONITORING_DEFAULTS.COMPANY_ID;
                newFilters.estate_id = undefined;
                newFilters.division_id = undefined;
            } else if (field === 'budget_year' || field === 'budget_month') {
                newFilters[field] = value as string;
            }

            setLocalFilters(newFilters);
        },
        [localFilters]
    );

    const handleApplyFilters = useCallback(() => {
        // Pass localFilters directly to onApplyFilters
        onApplyFilters(localFilters);
    }, [localFilters, onApplyFilters]);

    const handleClearFilters = useCallback(() => {
        const now = new Date();
        const clearedFilters: UpkeepBudgetFilters = {
            revision: UPKEEP_MONITORING_DEFAULTS.REVISION,
            budget_year: now.getFullYear().toString(),
            budget_month: String(now.getMonth() + 1).padStart(2, '0'),
            company_id: UPKEEP_MONITORING_DEFAULTS.COMPANY_ID,
            estate_id: undefined,
            division_id: undefined,
        };
        setLocalFilters(clearedFilters);
        onFilterChange(clearedFilters);
        onClearFilters();
    }, [onFilterChange, onClearFilters]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    return (
        <Box
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.100"
        >
            <VStack spacing={4} align="stretch">
                {/* Header */}
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                    <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color="gray.700">
                        Filter Budget Monitoring
                    </Text>
                    <HStack spacing={2}>
                        {onRefresh && (
                            <Tooltip label="Refresh Data" hasArrow>
                                <IconButton
                                    aria-label="Refresh data"
                                    icon={<MdRefresh />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={onRefresh}
                                    colorScheme="blue"
                                />
                            </Tooltip>
                        )}
                        {onExport && (
                            <Tooltip label={isExporting ? "Sedang mengekspor Excel..." : "Export ke Excel"} hasArrow>
                                <IconButton
                                    aria-label="Export data"
                                    icon={<FiDownload />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={onExport}
                                    isLoading={isExporting}
                                    colorScheme="green"
                                />
                            </Tooltip>
                        )}
                    </HStack>
                </Flex>

                {/* Filter Fields */}
                <Grid
                    templateColumns={{
                        base: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(5, 1fr)',
                    }}
                    gap={4}
                >
                    {/* Budget Year */}
                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Tahun
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            value={localFilters.budget_year}
                            onChange={(e) => handleInputChange('budget_year', e.target.value)}
                            onKeyDown={handleKeyPress}
                        >
                            {yearOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Budget Month */}
                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Bulan
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            value={localFilters.budget_month}
                            onChange={(e) => handleInputChange('budget_month', e.target.value)}
                            onKeyDown={handleKeyPress}
                        >
                            {monthOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Estate ID */}
                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Company
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder="Pilih Company"
                            value={localFilters.company_id ?? ''}
                            onChange={(e) => handleInputChange('company_id', e.target.value)}
                            onKeyDown={handleKeyPress}
                        >
                            {companies.map((company) => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Estate
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder="Semua Estate"
                            value={localFilters.estate_id || ''}
                            onChange={(e) => handleInputChange('estate_id', e.target.value)}
                        >
                            {filteredEstates.map((estate) => (
                                <option key={estate.id} value={estate.odoo_id}>
                                    {estate.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Division ID */}
                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>
                            Divisi
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder="Semua Divisi"
                            value={localFilters.division_id || ''}
                            onChange={(e) => handleInputChange('division_id', e.target.value)}
                            isDisabled={!localFilters.estate_id || divisions.length === 0}
                        >
                            {divisions.map((division) => (
                                <option key={division.id} value={division.odoo_id}>
                                    {division.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Action Buttons */}
                <Flex
                    gap={2}
                    justify={{ base: 'stretch', md: 'flex-end' }}
                    direction={{ base: 'column', sm: 'row' }}
                >
                    <Button
                        leftIcon={<MdClear />}
                        onClick={handleClearFilters}
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        borderRadius="lg"
                        flex={{ base: 1, sm: 'initial' }}
                    >
                        Reset
                    </Button>
                    <Button
                        leftIcon={<FiSearch />}
                        onClick={handleApplyFilters}
                        size="sm"
                        colorScheme="blue"
                        borderRadius="lg"
                        flex={{ base: 1, sm: 'initial' }}
                    >
                        Terapkan Filter
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
};

export default FilterUpkeepMonitoring;
